import { eligibleWhenEnabled } from 'src/lib/flow/eligibleWhenEnabled';
import { disabledPage, experimentPage } from 'src/lib/flow/experimentPage';
import { FlowPage } from 'src/lib/flow/FlowPage';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { addPageLoader, FlowPageLoader } from 'src/lib/flow/loaders';
import { checkValidationForActiveQuestions } from 'src/lib/flow/validation';
import { api } from 'src/store/api';
import { loadExperiment } from 'src/store/apiUtils';
import { getFlowFromState } from 'src/store/flow_listeners';
import { updateInputContext } from 'src/store/flow_ng';

import type { GetValidataQuery_validata_getConfidenceSignalsByFields_confidenceSignals } from '../../../../api/queries/__generated__/GetValidataQuery';
import basicDataPage from './basicDataPage';

const confidenceAttributeToQuestionKeyMap = {
  bedrooms: 'home.bedrooms',
  bathrooms_full: 'home.bathrooms.full',
  bathrooms_half: 'home.bathrooms.half',
  above_grade_sq_ft: 'home.above_grade_sq_ft',
  lot_sq_ft: 'home.lot_sq_ft',
  exterior_stories: 'home.exterior_stories',
  basement_finished_sq_ft: 'home.basement_finished_sq_ft',
  basement_unfinished_sq_ft: 'home.basement_unfinished_sq_ft',
  year_built: 'home.year_built',
  garage_spaces: 'home.garage_spaces',
  carport_spaces: 'home.carport_spaces',
};

/**
 * This is a list of validata keys that when they come back as LOW confidence
 * and reason CONFIDENCE_REASON_NO_DATA then we will ignore them because
 * usually homes can have a legitimate reason for not having this data.
 */
const ignoreConfidenceNoData = [
  'garage_spaces',
  'carport_spaces',
  'bathrooms_half',
  'basement_finished_sq_ft',
  'basement_unfinished_sq_ft',
];

const basementRelatedKeys = [
  'home.basement_finished_sq_ft',
  'home.basement_unfinished_sq_ft',
  'home.has_basement',
];
const parkingRelatedKeys = [
  'home.garage_spaces',
  'home.carport_spaces',
  'home.covered_parking_type',
];

// exported for testing
export function getVisibleQuestions(
  questions: FlowQuestion[],
  questionKeys: Array<string>,
): string[] {
  return questions
    .filter((question) => questionKeys.includes(question.key) && !question.hidden)
    .map((question) => question.key);
}

const lowConfidenceBasicDataPageEnabled: FlowPage['isEnabled'] = (ctx) => {
  //If we have between 1-5 visible low confidence questions then display this page
  const total = getVisibleQuestions(
    basicDataPage.questions(ctx),
    ctx.inputContext.lowConfidenceQuestions || [],
  ).length;

  return 1 <= total && total <= 5;
};

// exported for testing
export const filterLowConfidenceQuestions = (
  keys: Array<string>,
  allQuestions: Array<FlowQuestion>,
): Array<FlowQuestion> => {
  const hasBasementQuestions = keys.some((key) => basementRelatedKeys.includes(key));
  const hasParkingQuestions = keys.some((key) => parkingRelatedKeys.includes(key));

  const lowConfidenceQuestions = allQuestions.filter((question) => {
    if (keys.includes(question.key)) {
      return true;
    }
    if (hasBasementQuestions && basementRelatedKeys.includes(question.key)) {
      return true;
    }
    if (hasParkingQuestions && parkingRelatedKeys.includes(question.key)) {
      return true;
    }
    return false;
  });
  return lowConfidenceQuestions;
};

export const lowConfidenceBasicDataPage: FlowPage = {
  slug: () => 'basic-data-help',
  title: () => 'We found it! Mind helping us with these details?',
  subtitle: () => 'You’ll be able to review the rest of your home next.',
  subtitleElement: (experimentFlags) => {
    if (experimentFlags.experimentFlags.addressEntryConfirmation === 'treatment') {
      return;
    }
    return {
      type: 'element',
      key: 'address_map',
      component: 'address_map',
    };
  },
  isEnabled: lowConfidenceBasicDataPageEnabled,
  autoAdvance: () => false,
  questions: (ctx): Array<FlowQuestion> => {
    const allQuestions = basicDataPage.questions(ctx);
    const keys = ctx.inputContext.lowConfidenceQuestions ?? [];
    return filterLowConfidenceQuestions(keys, allQuestions);
  },
};

const detailsPage = experimentPage('lowConfidenceBasicData', {
  control: disabledPage('basic-data-help'),
  treatment: lowConfidenceBasicDataPage,
});

// exported for testing
export const filterLowConfidenceLevels = (
  data: GetValidataQuery_validata_getConfidenceSignalsByFields_confidenceSignals,
): boolean =>
  data.confidenceLevel === 'LOW' &&
  data.attributeName in confidenceAttributeToQuestionKeyMap &&
  !(
    data.reason == 'CONFIDENCE_REASON_NO_DATA' &&
    ignoreConfidenceNoData.includes(data.attributeName)
  );

const loadBasicDataHelpData: FlowPageLoader = async (listenerApi) => {
  const experiments: Array<ReturnType<typeof loadExperiment>> = [];

  // Fetch confidence data, wait for the promise to resolve
  const appState = listenerApi.getState();
  const addressToken = appState.sellerInput.address.uuid;
  const actionResult = await listenerApi.dispatch(api.endpoints.getValidata.initiate(addressToken));
  const allConfidenceData = actionResult.data ?? [];

  // find question keys that are low confidence
  const lowConfidenceQuestionKeys: Array<string> = allConfidenceData
    .filter(filterLowConfidenceLevels)
    .map((data) => confidenceAttributeToQuestionKeyMap[data.attributeName]);

  // also include question keys that are marked as invalid
  const { context } = getFlowFromState(listenerApi.getState());
  const validations = checkValidationForActiveQuestions(
    basicDataPage.questions(context),
    context.answers,
  );
  const invalidKeys = validations.invalid.map((question) => question.key);

  // de-duplicate question keys
  const allKeys = Array.from(new Set(lowConfidenceQuestionKeys.concat(invalidKeys)));

  // Save confidence data to Redux
  listenerApi.dispatch(
    updateInputContext({
      ...appState.flow_ng.inputContext,
      lowConfidenceQuestions: allKeys,
    }),
  );

  if (eligibleWhenEnabled(lowConfidenceBasicDataPageEnabled)(listenerApi)) {
    experiments.push(loadExperiment(listenerApi, 'lowConfidenceBasicData', true));
  }

  // Wait for experiments to finish loading
  if (experiments.length) {
    await Promise.all(experiments);
  }
};

addPageLoader('basic-data-help', 'load-experiments', loadBasicDataHelpData);

export default detailsPage;
